// Chapter 28 - Token Economy, Group Contingencies, and Contingency Contracting
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 28.

import type { ChapterModule } from "./types";

const chapter28Module: ChapterModule = {
  meta: {
    id: "28",
    number: 28,
    title: "Token Economy, Group Contingencies, and Contingency Contracting",
    subtitle: "Conditioned generalized reinforcers, independent / interdependent / dependent contingencies",
    part: "Part 11 - Special Applications",
    estMinutes: 70,
    bacb: ["G-17", "G-18"],
  },
  curated: true,
  overview: [
    "Chapter 28 covers three behavior change technologies that combine principles into systems. TOKEN ECONOMIES use tokens (generalized conditioned reinforcers) backed by a menu of items, activities, and privileges. GROUP CONTINGENCIES deliver a common consequence based on the behavior of one, some, or all members of a group (independent, dependent, interdependent). CONTINGENCY CONTRACTS document a task-and-reward agreement.",
    "You will learn the six steps to design a token economy, level systems, the three group-contingency types with selection guidelines, the components required in a contingency contract (task and reward, each specifying who/what/when/how well), and special cases (self-contracts, contracts for nonreaders).",
  ],
  objectives: [
    { id: "G-17", text: "Design and implement token economies with appropriate exchange ratios and backup reinforcers." },
    { id: "G-18", text: "Use group contingencies and contingency contracts effectively." },
  ],
  coreConcepts: [
    {
      title: "Token economy: three components",
      body: [
        "A token economy is a behavior change system with three major components: (a) a specified list of TARGET BEHAVIORS to be reinforced; (b) TOKENS or points that participants receive for emitting the target behaviors; and (c) a MENU of items, activities, privileges, and backup reinforcers exchangeable for tokens.",
      ],
    },
    {
      title: "Tokens as generalized conditioned reinforcers",
      body: [
        "Tokens are objects, symbols, marks, or chips that function as generalized conditioned reinforcers because they have been paired with a wide variety of backup reinforcers. Their effectiveness does not depend on any single MO.",
      ],
    },
    {
      title: "Level systems",
      body: [
        "A level system is a type of token economy in which participants move UP (or DOWN) a hierarchy of levels contingent on meeting performance criteria. Higher levels include more privileges and expect more independence.",
      ],
    },
    {
      title: "Six steps for designing a token economy",
      body: [
        "(1) Identify target behaviors and rules; (2) Select tokens (the medium of exchange); (3) Select a menu of backup reinforcers; (4) Establish a token exchange ratio; (5) Write procedures for dispensing/exchanging tokens and for failure to meet token criteria; (6) FIELD-TEST the system before full-scale implementation.",
      ],
    },
    {
      title: "Group contingencies: three types",
      body: [
        "A group contingency delivers a common consequence based on group behavior. (a) INDEPENDENT: each member earns the consequence based on their OWN behavior. (b) DEPENDENT: the entire group earns the consequence based on the behavior of ONE member (or a subset). (c) INTERDEPENDENT: the entire group earns the consequence based on the COLLECTIVE behavior of all members.",
      ],
    },
    {
      title: "Six guidelines for group contingencies",
      body: [
        "(a) Choose a powerful reward; (b) determine the target behavior(s) and collateral behaviors that might be affected; (c) set appropriate performance criteria; (d) combine with other procedures when appropriate; (e) select the most appropriate group contingency type; (f) monitor both individual and group performance.",
      ],
    },
    {
      title: "Contingency contracting",
      body: [
        "A contingency contract documents a contingent relation between completing a target behavior and access to or delivery of a specified reward. Two required components: TASK (who, what, when, how well) and REWARD (who, what, when, how much). An optional TASK RECORD tracks progress and supports interim rewards.",
      ],
    },
    {
      title: "Self-contracts and contracts for nonreaders",
      body: [
        "A self-contract is a contingency contract one makes with oneself: self-selected task and reward, self-monitoring of completion, self-delivery of the reward. Contracts for nonreaders use symbols, pictures, photos, and audio recordings to specify tasks and rewards.",
      ],
    },
  ],
  glossary: [
    { term: "Token economy", definition: "Behavior change system with target behaviors, tokens, and a menu of backup reinforcers exchangeable for tokens.", sourceHint: "A token economy, also called a token reinforcement system, is a behavior change system consisting of three major components: (a) a specified list of target behaviors to be reinforced; (b) tokens or points that participants receive for emitting the target behaviors; and (c) a menu of items or activities, privileges, and backup reinforcers from which participants choose and obtain by exchanging tokens they have earned." },
    { term: "Token", definition: "Object, symbol, mark, or chip that functions as a generalized conditioned reinforcer due to pairing with many backup reinforcers.", sourceHint: "Tokens are objects, symbols, marks, or chips that function as generalized conditioned reinforcers because they have been paired with a wide variety of backup reinforcers." },
    { term: "Level system", definition: "Token economy in which participants move up or down a hierarchy of levels contingent on performance criteria; higher levels yield more privileges.", sourceHint: "A level system is a type of token economy in which participants move up or down a hierarchy of levels contingent on meeting specific performance criteria with respect to the target behaviors." },
    { term: "Group contingency", definition: "A common consequence delivered based on the behavior of one, some, or all members of a group.", sourceHint: "In a group contingency a common consequence (usually, but not necessarily, a reward intended to function as reinforcement) is contingent on the behavior of one member of the group, the behavior of part of the group, or the behavior of everyone in the group." },
    { term: "Independent group contingency", definition: "Each member earns the consequence based on their OWN behavior.", sourceHint: "Group contingencies can be classified as independent, dependent, and interdependent." },
    { term: "Dependent group contingency", definition: "The entire group earns the consequence based on the behavior of ONE (or a subset of) member(s).", sourceHint: "Group contingencies can be classified as independent, dependent, and interdependent." },
    { term: "Interdependent group contingency", definition: "The entire group earns the consequence based on the COLLECTIVE behavior of all members.", sourceHint: "Group contingencies can be classified as independent, dependent, and interdependent." },
    { term: "Contingency contract", definition: "Document specifying a contingent relation between completion of a task and a reward; requires task and reward components.", sourceHint: "A contingency contract is a document that specifies a contingent relation between the completion of a target behavior and access to, or delivery of, a specified reward." },
    { term: "Task record", definition: "Optional component of a contingency contract that records progress and supports interim rewards.", sourceHint: "An optional task record provides a place for recording the progress of the contract and providing interim rewards." },
    { term: "Self-contract", definition: "Contingency contract one makes with oneself, including self-monitoring and self-delivery of reward.", sourceHint: "A self-contract is a contingency contract that an individual makes with herself, incorporating a self-selected task and reward as well as self-monitoring of task completion and self-delivery of the reward." },
  ],
  procedures: [
    {
      title: "Design a token economy (six steps)",
      intro: "Field-test before full implementation.",
      steps: [
        { title: "Step 1 - Target behaviors and rules", detail: "Identify what earns tokens; how many for each; rules for dispensing." },
        { title: "Step 2 - Select tokens", detail: "Durable, hard to counterfeit, easy to dispense and tally." },
        { title: "Step 3 - Backup reinforcers menu", detail: "Wide variety; updated regularly to avoid satiation." },
        { title: "Step 4 - Exchange ratio", detail: "Tokens-per-backup; tune to current reinforcer values; adjust as needed." },
        { title: "Step 5 - Procedures", detail: "When/how tokens are given; what happens when criteria not met; loss vs no-loss policies." },
        { title: "Step 6 - Field-test", detail: "Pilot with a small group; observe failure modes; revise before scaling." },
      ],
    },
    {
      title: "Pick the right group contingency",
      intro: "Match structure to the behavior target and team dynamics.",
      steps: [
        { title: "Independent", detail: "Use when individual differences matter and learners should not affect each other's consequences." },
        { title: "Dependent", detail: "Use when a single (or subset) learner's behavior is the lever; manage social pressure carefully." },
        { title: "Interdependent", detail: "Use to build collective behavior and shared accountability; effective for classroom-wide goals." },
        { title: "Set criteria + reward", detail: "Performance criteria explicit; reward powerful and accessible." },
        { title: "Monitor individuals and group", detail: "Watch for collateral behaviors and unintended social effects." },
      ],
    },
    {
      title: "Write a contingency contract",
      intro: "Two required components plus optional task record.",
      steps: [
        { title: "Task component", detail: "Specify WHO will do the task, WHAT the task is, WHEN it must be completed, and HOW WELL it must be done." },
        { title: "Reward component", detail: "Specify WHO will deliver the reward, WHAT the reward is, WHEN it will be delivered, and HOW MUCH." },
        { title: "Optional task record", detail: "Add a record for monitoring progress and providing interim rewards." },
        { title: "Both parties sign", detail: "Confirms agreement and adds verbal control over performance." },
        { title: "Review and revise", detail: "Adjust criteria, reward magnitude, or schedule based on performance." },
      ],
    },
  ],
  examples: [
    { scenario: "Classroom token economy where students earn checkmarks for on-task behavior and exchange them for end-of-week privileges.",
      application: "Token economy with checkmarks as generalized conditioned reinforcers.",
      sourceHint: "Tokens are objects, symbols, marks, or chips that function as generalized conditioned reinforcers." },
    { scenario: "A teacher offers the whole class a movie afternoon contingent on one specific student completing all assignments.",
      application: "Dependent group contingency. Manage social pressure on the named student carefully.",
      sourceHint: "Group contingencies can be classified as independent, dependent, and interdependent." },
    { scenario: "The class earns the prize when the AVERAGE score across all students reaches 90%.",
      application: "Interdependent group contingency. Collective behavior drives consequences.",
      sourceHint: "Group contingencies can be classified as independent, dependent, and interdependent." },
    { scenario: "A student signs a written agreement: 'I will complete all homework by Friday evening; if I do, I get to choose Saturday's movie.'",
      application: "Contingency contract with task and reward; both parties sign.",
      sourceHint: "Two components required in all contingency contracts are a description of the task and a description of the reward. Under task, who, what, when, and how well should be specified. Under reward, who, what, when, and how much should be specified.",
    },
  ],
  comparisons: [
    {
      title: "Independent vs Dependent vs Interdependent contingencies",
      left:  { label: "Independent", points: [
        "Each member earns based on OWN behavior.",
        "Low social pressure.",
      ]},
      right: { label: "Dependent / Interdependent", points: [
        "Dependent: whole group earns based on one/some members.",
        "Interdependent: whole group earns based on collective performance.",
        "Higher social pressure; can build cohesion or scapegoating.",
      ]},
    },
    {
      title: "Token economy vs Contingency contract",
      left:  { label: "Token economy", points: [
        "System-level: ongoing tokens, menu, exchange ratio.",
        "Best for classrooms or treatment programs.",
      ]},
      right: { label: "Contingency contract", points: [
        "Document-level: specific task-reward agreement.",
        "Best for individual goals; self-contracts possible.",
      ]},
    },
    {
      title: "Field-test vs Full deployment",
      left:  { label: "Field-test", points: [
        "Small-scale pilot.",
        "Surface failures before scaling.",
      ]},
      right: { label: "Full deployment", points: [
        "After field-test revisions.",
        "Includes plan for maintenance and removal.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "Tokens are just like cash.",
      reality: "Tokens function as generalized conditioned reinforcers only because they have been paired with many backups. Without backups, tokens lose function.",
      sourceHint: "Tokens are objects, symbols, marks, or chips that function as generalized conditioned reinforcers because they have been paired with a wide variety of backup reinforcers.",
    },
    {
      myth: "Group contingencies are always more efficient than individual ones.",
      reality: "Group contingencies introduce social dynamics (peer pressure, scapegoating) and may produce unintended collateral behaviors. Pick the type based on target, team, and risk profile.",
      sourceHint: "Six guidelines can assist the practitioner in implementing a group contingency: ... (b) determine the behavior to change and any collateral behaviors that might be affected.",
    },
    {
      myth: "A contingency contract is just a written reward chart.",
      reality: "Contracts require BOTH task and reward components, each specifying who, what, when, and how well/how much. Reward charts often lack the precision required.",
      sourceHint: "Two components required in all contingency contracts are a description of the task and a description of the reward. Under task, who, what, when, and how well should be specified. Under reward, who, what, when, and how much should be specified.",
    },
    {
      myth: "Token economies should be deployed as soon as designed.",
      reality: "FIELD-TEST first. Pilot a small group, surface failure modes, revise before scaling.",
      sourceHint: "Six basic steps in designing a token economy: ... (6) field-testing the system before full-scale implementation.",
    },
  ],
  examChecklist: [
    "Define a token economy and its three components.",
    "Explain why tokens function as generalized conditioned reinforcers.",
    "Describe level systems.",
    "List the six steps for designing a token economy.",
    "Define and compare independent, dependent, and interdependent group contingencies.",
    "List six guidelines for group contingencies.",
    "Define a contingency contract and its required components.",
    "Describe self-contracts and contracts for nonreaders.",
  ],
  flashcardTerms: [
    "Token economy", "Token", "Level system",
    "Group contingency", "Independent group contingency", "Dependent group contingency", "Interdependent group contingency",
    "Contingency contract", "Task record", "Self-contract",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Tokens in a token economy function as:",
      options: [
        { label: "A", text: "Unconditioned reinforcers" },
        { label: "B", text: "Generalized conditioned reinforcers" },
        { label: "C", text: "Discriminative stimuli only" },
        { label: "D", text: "Establishing operations" },
      ],
      answer: "B",
      explanation: "Tokens are paired with many backup reinforcers so their function does not depend on any single MO; they are generalized conditioned reinforcers.",
      topic: "Tokens",
      sourceHint: "Tokens are objects, symbols, marks, or chips that function as generalized conditioned reinforcers because they have been paired with a wide variety of backup reinforcers.",
    },
    {
      id: "Q2",
      question: "Which type of group contingency awards consequences based on the COLLECTIVE behavior of all members?",
      options: [
        { label: "A", text: "Independent" },
        { label: "B", text: "Dependent" },
        { label: "C", text: "Interdependent" },
        { label: "D", text: "Sequential" },
      ],
      answer: "C",
      explanation: "Interdependent group contingencies use collective performance as the contingency basis.",
      topic: "Group contingencies",
      sourceHint: "Group contingencies can be classified as independent, dependent, and interdependent.",
    },
    {
      id: "Q3",
      question: "Which is a required component of a contingency contract?",
      options: [
        { label: "A", text: "Task description (who, what, when, how well)" },
        { label: "B", text: "Reinforcer assessment data" },
        { label: "C", text: "A specific schedule of reinforcement (e.g., FR5)" },
        { label: "D", text: "Mandatory legal review" },
      ],
      answer: "A",
      explanation: "Required components are a task description (who/what/when/how well) and a reward description (who/what/when/how much).",
      topic: "Contingency contract",
      sourceHint: "Two components required in all contingency contracts are a description of the task and a description of the reward. Under task, who, what, when, and how well should be specified.",
    },
    {
      id: "Q4",
      question: "Step 6 of designing a token economy is:",
      options: [
        { label: "A", text: "Identify target behaviors" },
        { label: "B", text: "Field-test the system before full-scale implementation" },
        { label: "C", text: "Select tokens" },
        { label: "D", text: "Establish a token exchange ratio" },
      ],
      answer: "B",
      explanation: "Field-testing is the final design step before scaling the token economy.",
      topic: "Token economy design",
      sourceHint: "(6) field-testing the system before full-scale implementation.",
    },
    {
      id: "Q5",
      question: "A self-contract differs from a standard contract because:",
      options: [
        { label: "A", text: "It is not a behavior change tool" },
        { label: "B", text: "The individual writes, monitors, and delivers their own reward" },
        { label: "C", text: "It uses pictures only" },
        { label: "D", text: "It requires a token economy backbone" },
      ],
      answer: "B",
      explanation: "Self-contracts include self-selected task and reward, self-monitoring, and self-delivery.",
      topic: "Self-contracts",
      sourceHint: "A self-contract is a contingency contract that an individual makes with herself, incorporating a self-selected task and reward as well as self-monitoring of task completion and self-delivery of the reward.",
    },
  ],
  reviewPrompts: [
    "Design a six-step token economy for a classroom you know.",
    "Pick a group contingency type for a specific classroom goal and justify the choice.",
    "Write a contingency contract with all required components for one of your goals.",
    "Sketch a self-contract that includes self-monitoring and self-delivery.",
    "Explain why a token economy without varied backup reinforcers may fail.",
  ],
  recap: [
    "Token economies combine target behaviors, tokens (generalized conditioned reinforcers), and a menu of backups. Design follows six steps and ends with field-testing. Level systems add a hierarchy of privileges contingent on performance criteria.",
    "Group contingencies deliver shared consequences. Independent, dependent, and interdependent variants offer different individual/collective accountability profiles. Six guidelines structure the design.",
    "Contingency contracts document task-and-reward agreements; both components must specify who, what, when, and how well/how much. Self-contracts and contracts for nonreaders extend the technology to additional populations.",
  ],
  sourceExcerpts: [
    {
      heading: "Token economy components",
      text: "A token economy, also called a token reinforcement system, is a behavior change system consisting of three major components: (a) a specified list of target behaviors to be reinforced; (b) tokens or points that participants receive for emitting the target behaviors; and (c) a menu of items or activities, privileges, and backup reinforcers from which participants choose and obtain by exchanging tokens they have earned.",
    },
    {
      heading: "Six steps",
      text: "There are six basic steps in designing a token economy: (1) identifying the target behaviors and rules; (2) selecting the tokens that will serve as a medium of exchange; (3) selecting a menu of backup reinforcers; (4) establishing a token exchange ratio; (5) writing procedures to specify when and how tokens will be dispensed and exchanged and what will happen if the requirements to earn a token are not met; and (6) field-testing the system before full-scale implementation.",
    },
    {
      heading: "Group contingencies",
      text: "In a group contingency a common consequence (usually, but not necessarily, a reward intended to function as reinforcement) is contingent on the behavior of one member of the group, the behavior of part of the group, or the behavior of everyone in the group. Group contingencies can be classified as independent, dependent, and interdependent.",
    },
    {
      heading: "Contingency contract components",
      text: "Two components required in all contingency contracts are a description of the task and a description of the reward. Under task, who, what, when, and how well should be specified. Under reward, who, what, when, and how much should be specified. An optional task record provides a place for recording the progress of the contract and providing interim rewards.",
    },
  ],
};

export default chapter28Module;
